import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PartyCard } from "../components/ui/PartyCard";
import { apiClient } from "../lib/api";
import type { Party } from "../lib/types";

const PAGE_SIZE = 20;

export function Leaderboard() {
  const { t } = useTranslation();
  const [parties, setParties] = useState<Party[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    apiClient
      .get<{ parties: Party[]; total: number }>(
        `/api/parties/public?page=${page}&limit=${PAGE_SIZE}`,
      )
      .then((res) => {
        setParties(res.parties);
        setTotal(res.total);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [page]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const offset = (page - 1) * PAGE_SIZE;

  return (
    <main className="min-h-screen bg-stone-50 dark:bg-stone-950 text-stone-900 dark:text-stone-100 pt-14">
      <div className="max-w-2xl mx-auto px-4 py-12 flex flex-col gap-8">
        <div>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition-colors text-sm mb-4"
          >
            {t("nav.back")}
          </Link>
          <h1 className="text-3xl font-bold">{t("leaderboard.heading")}</h1>
          {total > 0 && (
            <p className="text-stone-500 dark:text-stone-400 text-sm mt-1">
              {t("leaderboard.party", { count: total })}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <div className="w-5 h-5 rounded-full border-2 border-stone-300 dark:border-stone-700 border-t-amber-500 animate-spin" />
          </div>
        ) : parties.length === 0 ? (
          <div className="text-stone-400 dark:text-stone-600 text-sm">
            {t("leaderboard.noParties")}
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {parties.map((party, i) => {
                const rank = offset + i + 1;
                const rankEl =
                  rank === 1 ? (
                    <span className="text-xl">🥇</span>
                  ) : rank === 2 ? (
                    <span className="text-xl">🥈</span>
                  ) : rank === 3 ? (
                    <span className="text-xl">🥉</span>
                  ) : (
                    <span className="text-stone-400 dark:text-stone-600 text-sm font-semibold">
                      #{rank}
                    </span>
                  );
                return (
                  <PartyCard
                    key={party.id}
                    party={party}
                    left={
                      <div className="w-7 text-center flex-shrink-0">
                        {rankEl}
                      </div>
                    }
                  />
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  {t("leaderboard.previous")}
                </button>
                <span className="text-stone-500 dark:text-stone-400 text-sm">
                  {t("leaderboard.page", { page, total: totalPages })}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  {t("leaderboard.next")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
